import { useContext } from 'react';
import { Route } from 'react-router';
import { Redirect, Switch } from 'react-router-dom';
import universal from 'react-universal-component';

import { ConfigContext } from 'Context/config';

import routing, {
  AFFILIATE_PAGE_ROUTES,
  CAR_PRIZE_DRAW_ROUTE,
  EARLY_DOCS_COLLECTION,
  EARLY_DOCS_VERIFICATION,
  FINANCE_PAYMENT_DETAILS_ROUTE,
  FINANCE_PAYMENT_DETAILS_ROUTE_MORE_FUNDS_REQUESTED,
  GUIDE_ROUTES,
  HANDSHAKE_ROUTE,
  HOME_COMPONENT_ROUTES,
  HOME_PAGE_ROUTES,
  POST_SALE_DOCS_COLLECTION,
  SERVICE_FEES_ROUTE,
  SITEMAP_HTML_URL,
  STORY_ROUTES,
  VEHICLE_COLLECTION_ROUTE,
  VERIFY_YOUR_IDENTITY_ROUTE,
} from 'Utilities/routing';
import { HELP_URL, PRO_URL } from 'Utilities/urls';
import {
  LANDING_PAGE_ROUTES,
  MOT_PAGE_ROUTES,
  NEW_LANDING_PAGE_ROUTES,
  PART_EXCHANGE_PATH,
  SELL_YOUR_CAR_CITY_ROUTES,
  TAX_PAGE_ROUTES,
  ULEZ_PAGE_ROUTES,
  WORDPRESS_ROUTES,
} from 'Utilities/wordpress';

import { PAAS_ROUTE } from 'PaaS/paas.constants';

import type { FeatureFlagsType } from '../../../../client/index.helper';
import PageLoader from '../../../components/transitions/PageLoader/PageLoader';
import PageLoaderError from '../../../components/transitions/PageLoaderError/PageLoaderError';
import { StoreContext } from '../../../store';
import { CUSTOMER_HUB_PATHS } from '../customerHub/CustomerHubHelpers';
import { IncodeSDKPageHOC } from '../documentCapture/uploadDocuments/pages/IncodeSDKPage/IncodeSDKPageHOC';
import { UploadDocumentsVerification } from '../documentCapture/uploadDocuments/pages/UploadDocumentsVerification/UploadDocumentsVerification';
import { VerifyYourIdentityPage } from '../documentCapture/uploadDocuments/pages/VerifyYourIdentityPage/VerifyYourIdentityPage';
import Status404 from '../error/404';
import ErrorBoundary from '../error/ErrorBoundary';
import Home from '../fastlane/home/Home';
import VehicleHeader from '../fastlane/vehicleHeaderWrapper/VehicleHeaderWrapper';
import Footer from '../footer/Footer';
import MetaTitle from '../metaTitle/MetaTitle';
import { SEO_PAGES_ROUTES } from '../SeoPages/helpers';
import { TRADE_DISCONTINUED_PATH, useTradeDiscontinuedRedirect } from '../tradeDiscontinued/useTradeDiscontinuedRedirect';
import { INELIGIBLE_URL } from '../valuations/valuations.constants';
import { ZendeskWidget } from '../zendeskWidget/ZendeskWidget';

import styles from './Routes.module.scss';

const universalImportOptions = {
  error: <PageLoaderError />,
  ignoreBabelRename: true,
  loading: <PageLoader />,
  onError: (error: any, { isServer }: { isServer: boolean }) => {
    if (isServer === false && window.navigator.onLine && window.Sentry) {
      window?.Sentry?.captureException(new Error(error), {
        fingerprint: ['Webpack Chunk Load Error'],
      });
    }
  },
};

const vehicleHeaderLoader = { ...universalImportOptions, loading: (<VehicleHeader loading />) };
const signInPageOptions = { ...universalImportOptions, loading: (<PageLoader showFooter='full' />) };

/**
 * NOTE - It's important to use the universal imports to properly code split
 * For example if Home was a standard import, the buyer logo's it includes get's bundled into the vendor bundle
 * even if one logo is only used only in Home
 */
const ValuationsRouter = universal(() => import(/* webpackPrefetch: true */ '../valuations/Pages/ValuationsRouter'), { ...vehicleHeaderLoader, key: (module) => module.ValuationsRouter });
const AcceptOffer = universal(() => import('../acceptOffer/AcceptOffer'), vehicleHeaderLoader);

const AccountPage = universal(() => import('../account/Pages/AccountPage/AccountPage'), universalImportOptions);
const SignIn = universal(() => import('../signIn/SignIn'), signInPageOptions);
const TokenExpired = universal(() => import('../fastlane/tokenExpired/TokenExpired'), universalImportOptions);
const UnsubscribePage = universal(() => import('../UnsubscribePage/UnsubscribePage'), universalImportOptions);
const LandingPages = universal(() => import('../contentPages/landingPages/LandingPages'), universalImportOptions);
const NewLandingPages = universal(() => import('../landingPages/Pages/LandingPage'), universalImportOptions);
const LandingPageClassic = universal(() => import('../landingPages/Pages/LandingPageClassic'), universalImportOptions);
const ServiceFees = universal(() => import('../landingPages/Pages/ServiceFees/ServiceFees'), universalImportOptions);
const CarPrizeDraw = universal(() => import('../landingPages/Pages/CarPrizeDraw/CarPrizeDraw'), universalImportOptions);
const CheckerRouter = universal(() => import('../checkerPages/Pages/CheckerRouter'), universalImportOptions);
const UlezPage = universal(() => import('../contentPages/ulezPage/UlezPage'), universalImportOptions);
const BrandsContent = universal(() => import('../contentPages/brands/Brands'), universalImportOptions);
const HowItWorks = universal(() => import('../fastlane/howItWorks/HowItWorks'), universalImportOptions);
const ReviewsContent = universal(() => import('../contentPages/reviews/Reviews'), universalImportOptions);
const WordpressContent = universal(() => import('../contentPages/wordpress/Wordpress'), universalImportOptions);
const WordpressPages = universal(() => import('../contentPages/wordPressPages/WordpressPages'), universalImportOptions);
const AboutPage = universal(() => import('../contentPages/aboutUs/Page'), universalImportOptions);
const UnsupportedBrowser = universal(() => import('../../../components/misc/unsupportedBrowser/UnsupportedBrowser'), universalImportOptions);
const VehicleProfiling = universal(() => import('../vehicleProfiling/VehicleProfiling'), { ...universalImportOptions, key: (module) => module.VehicleProfiling });
const VehicleCollection = universal(() => import('../vehicleCollection/VehicleCollection'), { ...universalImportOptions, key: (module) => module.VehicleCollectionWithProviders });
const FinancePaymentDetails = universal(() => import('../postSale/financeSettlement/FinancePaymentDetails/FinancePaymentDetails'), { ...universalImportOptions, key: (module) => module.FinancePaymentDetailsWithProviders });
const FinancePaymentDetailsMoreFundsRequested = universal(() => import('../postSale/financeSettlement/FinancePaymentDetailsMoreFundsRequested/FinancePaymentDetailsMoreFundsRequested'), { ...universalImportOptions, key: (module) => module.FinancePaymentDetailsWithProviders });
const DocumentCapture = universal(() => import('../documentCapture/DocumentCapture'), universalImportOptions);
const AffiliatePage = universal(() => import('../affiliate/AffiliatePage/AffiliatePage'), universalImportOptions);
const Sitemap = universal(() => import('../sitemap/Sitemap'), universalImportOptions);
const CustomerHub = universal(() => import('../customerHub/Pages/CustomerHub'), universalImportOptions);
const SeoPage = universal(() => import('../SeoPages/SeoPage/SeoPage'), universalImportOptions);
const Refer = universal(() => import('../fastlane/refer/Refer'), universalImportOptions);
const VehicleDetailsPage = universal(() => import('../documentCapture/confirmSale/ConfirmationVehicleDetails/VehicleDetailsSection'), universalImportOptions);
const DocsEarly = universal(() => import('../documentCapture/uploadDocuments/DocsEarly/DocsEarly'), universalImportOptions);
const StandaloneBankDetailsPages = universal(() => import('../documentCapture/StandaloneBankDetails/StandaloneBankDetailsView'), universalImportOptions);
const Homepage = universal(() => import('../homepage/Pages/Homepage/Homepage'), universalImportOptions);
const Locations = universal(() => import('../locations/Locations'), universalImportOptions);
const IneligibleStatus = universal(() => import('../fastlane/valuationsIneligible/IneligibleStatus'), universalImportOptions);
const PaaS = universal(() => import('PaaS/Paas'), universalImportOptions);
const HandshakeComponent = universal(() => import('../postSale/handshake/Handshake'), universalImportOptions);
const TradeDiscontinued = universal(() => import('../tradeDiscontinued/TradeDiscontinued'), { ...universalImportOptions, key: (module) => module.TradeDiscontinued });

const nonStoreContextComponents = [
  [`${routing.vrmPath}`, ValuationsRouter],
  [['/dealers', '/pro', '/guides/motorway-for-dealers'], () => {
    window.location.href = PRO_URL;
    return null;
  }],
  [['/faq', '/FAQ'], () => {
    window.location.href = HELP_URL;
    return null;
  }],
  [HOME_COMPONENT_ROUTES, Home],
  [HOME_PAGE_ROUTES, Homepage],
  ['/about', AboutPage],
  ['/how-it-works', HowItWorks],
  ['/refer', Refer],
  ['/reviews', ReviewsContent],
  [`${routing.vrmPath}/accept-offer`, AcceptOffer],
  ['/sign-in', SignIn],
  ['/token-expired', TokenExpired],
  ['/unsupported-browser', UnsupportedBrowser],
  ['/sell-my-car/brands', BrandsContent],
  ['/locations', Locations],
  [EARLY_DOCS_COLLECTION, IncodeSDKPageHOC],
  [EARLY_DOCS_VERIFICATION, UploadDocumentsVerification],
  [
    [
      POST_SALE_DOCS_COLLECTION,
      `${routing.vrmPath}/confirm-sale-summary`,
      `${routing.vrmPath}/confirm-sale`,
      `${routing.vrmPath}/add-bank-details`,
      `${routing.vrmPath}/upload-documents/:kind?`,
      `${routing.vrmPath}/review-status`,
      `${routing.vrmPath}/purchasing-dealer-details`,
      `${routing.vrmPath}/finance-funds-received`, // Fake document capture route used for finance funds received
      `${routing.vrmPath}/sale-complete`, // Fake document capture route used for enhanced analytics
    ],
    DocumentCapture,
  ],
  [`${routing.vrmPath}${VEHICLE_COLLECTION_ROUTE}`, VehicleCollection],
  [`${routing.vrmPath}/vehicle-details`, VehicleDetailsPage],
  [`${routing.vrmPath}/profile/:step?/:step?/:step?`, VehicleProfiling],
  [AFFILIATE_PAGE_ROUTES, AffiliatePage],
  [ULEZ_PAGE_ROUTES, UlezPage],
  [SITEMAP_HTML_URL, Sitemap],
  [CUSTOMER_HUB_PATHS, CustomerHub],
  [SEO_PAGES_ROUTES, SeoPage],
  [
    [
      ...NEW_LANDING_PAGE_ROUTES,
      ...SELL_YOUR_CAR_CITY_ROUTES,
      PART_EXCHANGE_PATH,
      ...[...NEW_LANDING_PAGE_ROUTES, ...SELL_YOUR_CAR_CITY_ROUTES, PART_EXCHANGE_PATH].map((r) => `${r}/mileage`),
    ],
    NewLandingPages,
  ],
  ['/account', AccountPage],
  [`${routing.vrmPath}/${INELIGIBLE_URL}/`, IneligibleStatus],
  [`${routing.vrmPath}/get-ahead/upload-documents/:kind?`, DocsEarly],
  [
    [
      `${routing.vrmPath}/request/bank-details`,
      `${routing.vrmPath}/request/:kind?`,
      `${routing.vrmPath}/request/bank-details/confirmed`,
    ],
    StandaloneBankDetailsPages,
  ],
  [[...MOT_PAGE_ROUTES, ...TAX_PAGE_ROUTES], CheckerRouter],
  [`${routing.vrmPath}${FINANCE_PAYMENT_DETAILS_ROUTE}`, FinancePaymentDetails],
  [`${routing.vrmPath}${FINANCE_PAYMENT_DETAILS_ROUTE_MORE_FUNDS_REQUESTED}`, FinancePaymentDetailsMoreFundsRequested],
  [`${routing.vrmPath}/vehicle-payment`, DocumentCapture], // Post sale router using DocumentCapture
  [PAAS_ROUTE, PaaS],
  [HANDSHAKE_ROUTE, HandshakeComponent],
  [TRADE_DISCONTINUED_PATH, TradeDiscontinued],
  [VERIFY_YOUR_IDENTITY_ROUTE, VerifyYourIdentityPage],
  [[CAR_PRIZE_DRAW_ROUTE, `${CAR_PRIZE_DRAW_ROUTE}/mileage`], CarPrizeDraw],
];

export const useFeatureFlaggedRoutes = (featureFlags?: FeatureFlagsType) => {
  const flaggedRoutes = [];

  if (featureFlags?.showWordpressRefactoringUpdates) {
    flaggedRoutes.push([WORDPRESS_ROUTES, WordpressPages]);
  }

  if (featureFlags?.showLandingpageRefactoringUpdates) {
    flaggedRoutes.push([[
      ...LANDING_PAGE_ROUTES,
      ...LANDING_PAGE_ROUTES.map((r) => `${r}/mileage`),
    ], LandingPageClassic]);
  }

  if (featureFlags?.showServiceFee) {
    flaggedRoutes.push([SERVICE_FEES_ROUTE, ServiceFees]);
  }

  return flaggedRoutes;
};

const useWordpressContentRoutes = () => {
  const base = [
    '/blog/:year?/:month?/:day?/:post?',
    '/claims',
    '/cookies',
    '/guides/:subdir?/:author?',
    '/press',
    '/privacy',
    '/terms',
  ];

  return [...base, ...[...GUIDE_ROUTES, ...STORY_ROUTES]];
};

export const Routes = ({ hasError = false }) => {
  const { configState: { featureFlags } } = useContext(ConfigContext);

  useTradeDiscontinuedRedirect();

  const featureFlaggedRoutes = useFeatureFlaggedRoutes(featureFlags);
  const wordpressContentRoutes = useWordpressContentRoutes();

  const allRoutes = nonStoreContextComponents.concat(featureFlaggedRoutes);

  return (
    <>
      <MetaTitle />
      {
        !featureFlags?.hideZendeskWidget && (
          <ZendeskWidget />
        )
      }
      <div className={styles.main} id="main">
        {/* Move as many components as possible out of `<StoreContext/>` */}
        <ErrorBoundary forceShow={hasError}>
          <Switch>
            {allRoutes.map(([path, component]) => <Route key={path} exact {...{ component, path }} />)}
            {
              <Route exact path='/unsubscribe/:sms?'>
                <UnsubscribePage />
              </Route>
            }
          </Switch>
        </ErrorBoundary>
        <StoreContext.Consumer>
          {(context) => (
            <ErrorBoundary forceShow={hasError}>
              <Switch>
                {/* Placeholder components to not trigger a 404 */}
                {allRoutes.map(([path]) => <Route key={path} exact render={() => (<></>)} {...{ path }} />)}
                <Route exact path='/unsubscribe/:sms?' render={() => (<></>)} />
                {/* End Placeholder components to not trigger a 404 */}

                {!featureFlags?.showWordpressRefactoringUpdates && <Route
                  exact
                  path={wordpressContentRoutes}
                  render={(props) => (<WordpressContent {...Object.assign(props, { context })} />)}
                />}

                {!featureFlags?.showLandingpageRefactoringUpdates && <Route
                  exact
                  path={[...LANDING_PAGE_ROUTES, ...LANDING_PAGE_ROUTES.map((r) => `${r}/mileage`)]}
                  render={(props) => (<LandingPages {...Object.assign(props, { context })} />)}
                />}

                <Redirect exact from="/:vrm/:buyer/appointment/book" to={`${routing.vrmPath}`} />
                <Redirect exact from="/:vrm/:buyer/summary/:checkout?" to={`${routing.vrmPath}`} />
                <Redirect exact from={`${routing.vrmPath}/car-sold`} to={`${routing.vrmPath}`} />
                <Redirect exact from={`${routing.vrmPath}/sign-up`} to={`${routing.vrmPath}`} />

                <Route render={() => (<Status404 />)} />
              </Switch>
            </ErrorBoundary>
          )}
        </StoreContext.Consumer>
      </div>

      <Footer />
    </>
  );
};
